/**
 * Proof page (`/proofs/$proofId`): a pull request's acceptance criteria with live
 * status, each expandable to how it was checked.
 *
 * @module features/proofs/pages/ProofPage
 */
import React from 'react';
import { Banner, Button, EmptyState, Flex, ListCard, LoadingOrbit, Page, PageHeader, TagBadge, Text, useToast } from '@chatsdet/ui';
import { ArrowClockwise, GitCommit, GitPullRequest } from '@phosphor-icons/react';
import { useParams, useSearch } from '@tanstack/react-router';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { ApiError } from '../../../lib/api-error';
import { CriterionCard } from '../components/CriterionCard';
import { CheckTypeChips } from '../components/CheckTypeChips';
import { isStaleE2e, outcomeSummary } from '../evidenceKinds';
import { useProof, useRerunProof } from '../hooks/useProof';

const RERUN_DEADLINE_MS = 10 * 60 * 1000;

const PullRequestStateBadge: React.FC<{ mergedAt: string | null; abandonedAt: string | null }> = ({ mergedAt, abandonedAt }) => {
  if (mergedAt) return <TagBadge name="Merged" color="purple" hideDot />;
  if (abandonedAt) return <TagBadge name="Closed" color="gray" hideDot />;
  return <TagBadge name="Open" color="green" hideDot />;
};

export const ProofPage: React.FC = () => {
  const { proofId } = useParams({ from: '/proofs/$proofId' });
  const { c } = useSearch({ from: '/proofs/$proofId' });
  return <ProofContent key={proofId} proofId={proofId} criterion={c} />;
};

const ProofContent: React.FC<{ proofId: string; criterion: number | undefined }> = ({ proofId, criterion }) => {
  const { toast } = useToast();
  // True from pressing Re-run until the refetched proof has no stale criterion left
  // (a test that fails the re-run reads failed, not stale).
  const [isRerunning, setIsRerunning] = React.useState(false);
  const [rerunTimedOut, setRerunTimedOut] = React.useState(false);
  const query = useProof(proofId, isRerunning);
  const proof = query.data;
  const staleCount = proof?.evidence.filter((item) => isStaleE2e(item, proof.proof.headSha)).length ?? 0;
  const rerun = useRerunProof(proofId);
  React.useEffect(() => {
    if (isRerunning && staleCount === 0) setIsRerunning(false);
  }, [isRerunning, staleCount]);
  // Bound the polling: give up after RERUN_DEADLINE_MS. A reload loses the in-progress state.
  React.useEffect(() => {
    if (!isRerunning) return;
    const timer = setTimeout(() => {
      setIsRerunning(false);
      setRerunTimedOut(true);
    }, RERUN_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [isRerunning]);

  useBreadcrumbs([{ key: 'proof', label: proof ? `${proof.proof.repoFullName} #${proof.proof.prNumber}` : 'Proof' }]);

  if (query.isLoading) {
    return (
      <Page>
        <Flex justify="center" className="py-24">
          <LoadingOrbit size="lg" />
        </Flex>
      </Page>
    );
  }
  if (query.error instanceof ApiError && query.error.code === 'PROOF_NOT_FOUND') {
    return (
      <Page>
        <EmptyState title="Proof not found" description="We can't find this proof. It may not exist, or it may belong to another organization." />
      </Page>
    );
  }
  if (query.isError && !proof) {
    return (
      <Page>
        <Text color="muted">{query.error.message}</Text>
      </Page>
    );
  }
  if (!proof) return null;

  const { proof: record, verdict, evidence } = proof;
  const repoUrl = `https://github.com/${record.repoFullName}`;
  const prUrl = `${repoUrl}/pull/${record.prNumber}`;
  const summary = outcomeSummary(verdict.criteria);
  const closed = record.mergedAt !== null || record.abandonedAt !== null;
  const handleRerun = () =>
    rerun.mutate(undefined, {
      onSuccess: () => {
        setRerunTimedOut(false);
        setIsRerunning(true);
      },
      onError: (error) => toast.error(error.message),
    });

  return (
    <Page>
      <PageHeader
        icon={<GitPullRequest size={24} weight="fill" />}
        eyebrow={<PullRequestStateBadge mergedAt={record.mergedAt} abandonedAt={record.abandonedAt} />}
        title={
          <a
            href={prUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`${record.repoFullName} pull request ${record.prNumber} on GitHub`}
            className="hover:underline"
          >
            {`${record.repoFullName} #${record.prNumber}`}
          </a>
        }
        description={
          <Flex align="center" gap="sm" wrap>
            <span>{verdict.title}</span>
            <span aria-hidden>·</span>
            <a
              href={`${repoUrl}/commit/${record.headSha}`}
              target="_blank"
              rel="noreferrer"
              aria-label={`Commit ${record.headSha}`}
              className="inline-flex items-center gap-1 font-mono hover:text-foreground hover:underline"
            >
              <GitCommit size={14} aria-hidden />
              {record.headSha.slice(0, 7)}
            </a>
            <CheckTypeChips evidence={evidence} />
          </Flex>
        }
        actions={
          staleCount > 0 && !closed ? (
            <Button
              variant="outline"
              size="sm"
              isDisabled={rerun.isPending || isRerunning}
              onPress={handleRerun}
              iconLeft={isRerunning ? <LoadingOrbit size="sm" /> : <ArrowClockwise size={14} />}
            >
              {isRerunning ? 'Re-running…' : 'Re-run out-of-date checks'}
            </Button>
          ) : null
        }
      />
      <Flex direction="column" gap="sm" className="mt-4 max-w-4xl">
        {rerunTimedOut && <Banner variant="error">The re-run did not finish. Try again.</Banner>}
        {isRerunning && (
          <Banner variant="info">Re-running the out-of-date tests. This page updates by itself.</Banner>
        )}
        {summary && (
          <Text weight="semibold" data-testid="proof-summary">
            {summary}
          </Text>
        )}
        <ListCard title="Acceptance criteria">
          {verdict.criteria.map((item) => (
            <CriterionCard
              key={item.position}
              criterion={item}
              evidence={evidence.filter((e) => e.position === item.position)}
              defaultExpanded={item.position === criterion}
            />
          ))}
        </ListCard>
      </Flex>
    </Page>
  );
};
